// SuperAdminToolsTab.jsx — Admin Panel "Super Admin Tools" sub-tab, extracted
// from LimitedPartnerPortal. One-shot maintenance tools: review-quarter
// backfill, chapter-ranking recalc, legacy admin->lp role migration, and the
// anniversary/Founding LP badge backfill (which pulls the chapter-configurable
// founding window via normalizeFoundingWindow from ChapterAdminTab).
import React from "react";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "../../../../firebaseConfig.js";
import { RetroButton } from "../../../ui/retro";
import {
  calculateChapterRankings,
  getCurrentQuarter,
} from "../../../../services/statsTracking";
import { normalizeFoundingWindow } from "./ChapterAdminTab";

/**
 * Local helper used on the Super Admin Tools tab. Renders a Millennium-Bug
 * styled "tool card" — eyebrow label, display-serif heading, body copy,
 * optional footnote / warning, and a single call-to-action. Kept local
 * because the shape is specific to the admin tool rail.
 */
function AdminToolCard({ eyebrow, title, body, footnote, warning, action }) {
  return (
    <section className="admin-tool-card">
      {eyebrow && <div className="admin-tool-card__eyebrow">{eyebrow}</div>}
      <h5 className="admin-tool-card__title">{title}</h5>
      {body && <div className="admin-tool-card__body">{body}</div>}
      {footnote && <div className="admin-tool-card__footnote">{footnote}</div>}
      {warning && (
        <div className="admin-tool-card__warning" role="note">
          {warning}
        </div>
      )}
      {action && <div className="admin-tool-card__action">{action}</div>}
    </section>
  );
}

export default function SuperAdminToolsTab({
  users,
  isAdmin,
  isSuperAdmin,
  loadAdminData,
  showAppAlert,
  showAppConfirm,
}) {
// One-shot backfill: write `quarter` onto every existing review doc based on its pitch's quarter.
// Needed because quarter-scoped badges (Midas Touch, Completionist) can only see reviews where this field is set.
const handleBackfillReviewQuarters = async () => {
  if (!showAppConfirm('This will scan every review and write a `quarter` field (derived from the review\'s pitch). Safe to run multiple times. Continue?')) {
    return;
  }
  try {
    const reviewsSnapshot = await getDocs(collection(db, "reviews"));
    const pitchesSnapshot = await getDocs(collection(db, "pitches"));
    const pitchQuarterById = {};
    pitchesSnapshot.forEach(p => { pitchQuarterById[p.id] = p.data().quarter || null; });

    let written = 0;
    let skipped = 0;
    let missingPitch = 0;

    for (const reviewDoc of reviewsSnapshot.docs) {
      const review = reviewDoc.data();
      if (review.quarter) { skipped++; continue; }
      const q = pitchQuarterById[review.pitchId];
      if (!q) { missingPitch++; continue; }
      await updateDoc(doc(db, "reviews", reviewDoc.id), { quarter: q });
      written++;
    }

    showAppAlert(`Review quarter backfill complete.\n\nWritten: ${written}\nAlready had quarter: ${skipped}\nPitch not found: ${missingPitch}`);
  } catch (error) {
    console.error('Backfill review quarters error:', error);
    showAppAlert(`Backfill failed: ${error.message}`);
  }
};

// Recalculate chapter rankings for the current quarter across every chapter — unlocks
// Chapter MVP (isChapterLeaderThisQuarter) and Neighborhood Mayor (quarterlyTop3 >= 2).
// Safe to run any time; the stat writes are idempotent for the MVP flag, but quarterlyTop3
// increments every run — see note below in the UI.
const handleRecalculateChapterRankings = async () => {
  if (!showAppConfirm(`This will recalculate chapter-level leaderboards for ${getCurrentQuarter()} across every chapter.\n\nRun this once near the end of each quarter. Running it multiple times will over-increment the "top-3 finishes" counter. Continue?`)) {
    return;
  }
  try {
    // Collect the distinct set of chapters from users currently loaded
    const chapters = [...new Set(users.map(u => u.chapter).filter(Boolean))];
    if (chapters.length === 0) {
      showAppAlert('No chapters found in loaded user list. Load admin data first.');
      return;
    }
    for (const chapter of chapters) {
      await calculateChapterRankings(chapter);
    }
    showAppAlert(`Chapter rankings recalculated for ${chapters.length} chapter(s): ${chapters.join(', ')}`);
    loadAdminData();
  } catch (error) {
    console.error('Chapter rankings recalc error:', error);
    showAppAlert(`Recalculation failed: ${error.message}`);
  }
};

// One-time migration: any user doc still carrying role:'admin' from the pre-rename
// world is promoted to role:'lp' (post-rename lp == what admin used to mean). Safe
// to re-run; does nothing if no admin docs remain.
const handleMigrateAdminToLP = async () => {
  if (!isSuperAdmin) {
    showAppAlert("Only Super Admins can run this migration.");
    return;
  }
  if (!showAppConfirm("This will update every user with role='admin' to role='lp'. Safe to re-run. Continue?")) {
    return;
  }
  try {
    const usersSnapshot = await getDocs(collection(db, "users"));
    const toMigrate = usersSnapshot.docs.filter(d => d.data().role === 'admin');
    if (toMigrate.length === 0) {
      showAppAlert("No legacy 'admin' users found. Already migrated.");
      return;
    }
    for (const d of toMigrate) {
      await updateDoc(doc(db, "users", d.id), { role: 'lp' });
    }
    showAppAlert(`Migrated ${toMigrate.length} user(s) from 'admin' to 'lp':\n\n${toMigrate.map(d => d.data().email || d.id).join('\n')}`);
    loadAdminData();
  } catch (error) {
    console.error('Admin->LP migration error:', error);
    showAppAlert(`Migration failed: ${error.message}`);
  }
};

const handleAssignAnniversaryBadges = async () => {
  if (!showAppConfirm('This will check all users and assign Founding LP and Year Club badges to those who qualify based on their join dates. Continue?')) {
    return;
  }

  try {
    showAppAlert('Starting anniversary badge assignment...');

    // Load chapter founding dates + badge windows so the Founding LP check
    // is chapter-aware. Falls back to Jan 1 of foundedYear when foundedDate
    // isn't set on a chapter.
    const chaptersSnapshot = await getDocs(collection(db, "chapters"));
    const chapterFoundingByName = {};
    chaptersSnapshot.forEach((cDoc) => {
      const c = cDoc.data();
      let founding = null;
      if (c.foundedDate) {
        founding = c.foundedDate.toDate ? c.foundedDate.toDate() : new Date(c.foundedDate);
      } else if (typeof c.foundedYear === 'number') {
        founding = new Date(Date.UTC(c.foundedYear, 0, 1));
      }
      if (founding && !isNaN(founding.getTime()) && c.name) {
        chapterFoundingByName[c.name] = {
          founding,
          windowDays: normalizeFoundingWindow(c.foundingWindowDays),
        };
      }
    });

    // Get all users
    const usersSnapshot = await getDocs(collection(db, "users"));
    let processedCount = 0;
    let badgesAddedCount = 0;

    // Anniversary badge definitions. og_neighbor takes (joinDate, chapterInfo)
    // where chapterInfo carries the founding date + configured badge window;
    // year-club badges only need joinDate.
    const ANNIVERSARY_BADGES = {
      og_neighbor: {
        id: 'og_neighbor',
        name: '🏛️ Founding LP',
        description: "LP who joined during their chapter's founding window",
        category: 'general',
        checkFunction: (joinDate, chapterInfo) => {
          if (!chapterInfo) return false;
          const daysDiff = (joinDate.getTime() - chapterInfo.founding.getTime()) / (24 * 60 * 60 * 1000);
          return daysDiff >= 0 && daysDiff <= chapterInfo.windowDays;
        }
      },
      two_year_club: {
        id: 'two_year_club',
        name: '📅 2 Year Club',
        description: 'Active LP for 2 years',
        category: 'general',
        checkFunction: (joinDate) => {
          const yearsSince = (Date.now() - joinDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
          return yearsSince >= 2;
        }
      },
      three_year_club: {
        id: 'three_year_club',
        name: '🎂 3 Year Club',
        description: 'Active LP for 3 years',
        category: 'general',
        checkFunction: (joinDate) => {
          const yearsSince = (Date.now() - joinDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
          return yearsSince >= 3;
        }
      },
      four_year_club: {
        id: 'four_year_club',
        name: '🎊 4 Year Club',
        description: 'Active LP for 4 years',
        category: 'general',
        checkFunction: (joinDate) => {
          const yearsSince = (Date.now() - joinDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
          return yearsSince >= 4;
        }
      },
      five_year_club: {
        id: 'five_year_club',
        name: '🏅 5 Year Club',
        description: 'Active LP for 5 years',
        category: 'general',
        checkFunction: (joinDate) => {
          const yearsSince = (Date.now() - joinDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
          return yearsSince >= 5;
        }
      }
    };
    
    for (const userDoc of usersSnapshot.docs) {
      const userData = userDoc.data();
      const currentBadges = userData.badges || [];
      const currentBadgeIds = currentBadges.map(b => b.badgeId || b.id);
      
      // Skip if no anniversary date
      if (!userData.anniversary) {
        continue;
      }
      
      // Get join date
      const joinDate = userData.anniversary.toDate ? userData.anniversary.toDate() : new Date(userData.anniversary);
      
      // Check which badges they should have. og_neighbor needs the chapter's
      // founding date + window; year-club badges ignore the second arg.
      const newBadges = [];
      const userChapterInfo = chapterFoundingByName[userData.chapter] || null;

      for (const [badgeId, badge] of Object.entries(ANNIVERSARY_BADGES)) {
        if (!currentBadgeIds.includes(badgeId) && badge.checkFunction(joinDate, userChapterInfo)) {
          newBadges.push({
            badgeId: badge.id,
            earnedDate: new Date(),
            category: badge.category,
            name: badge.name,
            description: badge.description
          });
          badgesAddedCount++;
        }
      }
      
      // Update if new badges to add
      if (newBadges.length > 0) {
        await updateDoc(doc(db, 'users', userDoc.id), {
          badges: [...currentBadges, ...newBadges]
        });
      }
      
      processedCount++;
    }
    
    showAppAlert(`Anniversary badge assignment complete!\n\nUsers processed: ${processedCount}\nNew badges added: ${badgesAddedCount}`);
    
    // Reload admin data to refresh user list
    if (isAdmin) {
      loadAdminData();
    }
    
  } catch (error) {
    console.error('Error assigning anniversary badges:', error);
    showAppAlert(`Error: ${error.message}`);
  }
};

  return (
            <>
              <div className="win95-pane-header">
                <div>
                  <div className="win95-pane-header__context">
                    Maintenance · one-shot backfills and migrations
                  </div>
                  <h2 className="win95-pane-header__title">Super admin tools</h2>
                </div>
              </div>

              <div className="admin-pane-body">
              <div className="admin-tool-grid">
                <AdminToolCard
                  eyebrow="Migration"
                  title="Migrate legacy admin users"
                  body={
                    <>
                      Finds every user with <code>role: "admin"</code> and updates them to{' '}
                      <code>role: "lp"</code>. Your super admin account is unaffected.
                      Safe to re-run — does nothing if no legacy admins remain.
                    </>
                  }
                  action={
                    <RetroButton onClick={handleMigrateAdminToLP} variant="primary">
                      Run admin → lp migration
                    </RetroButton>
                  }
                />

                <AdminToolCard
                  eyebrow="Badges"
                  title="Anniversary badge assignment"
                  body="Assigns Founding LP and Year Club badges to all qualifying LPs based on their join dates."
                  footnote={
                    <ul>
                      <li>Founding LP — LPs who joined within their chapter's founding window (set per chapter in the Chapters tab)</li>
                      <li>2 / 3 / 4 / 5 Year Clubs — LPs active that many years</li>
                    </ul>
                  }
                  action={
                    <RetroButton onClick={handleAssignAnniversaryBadges} variant="primary">
                      Assign anniversary badges
                    </RetroButton>
                  }
                />

                <AdminToolCard
                  eyebrow="Backfill"
                  title="Backfill review quarters"
                  body={
                    <>
                      Writes a <code>quarter</code> field onto legacy review docs (derived from
                      each review's pitch). Required for Midas Touch, Completionist, and Perfect
                      Attendance badges to see historical activity. Safe to re-run — only touches
                      reviews that don't already have a quarter.
                    </>
                  }
                  action={
                    <RetroButton onClick={handleBackfillReviewQuarters} variant="primary">
                      Run review quarter backfill
                    </RetroButton>
                  }
                />

                <AdminToolCard
                  eyebrow="Leaderboards"
                  title={`Recalculate chapter rankings — ${getCurrentQuarter()}`}
                  body="Computes this quarter's top reviewer in every chapter and bumps each top-3 finisher's counter. Unlocks Chapter MVP and Neighborhood Mayor badges."
                  warning="Run this once near the end of each quarter. Running multiple times will over-increment the top-3 counter."
                  action={
                    <RetroButton onClick={handleRecalculateChapterRankings} variant="primary">
                      Recalculate rankings
                    </RetroButton>
                  }
                />
              </div>
              </div>
            </>
  );
}
